import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { HardDrives, Package, Buildings, ChartBar, TreeStructure, CurrencyInr } from '@phosphor-icons/react';
import { useAuth } from '../../App';
import { ModuleShell, ModuleSubnav } from '../../components/module-ui';
import InventoryPage from './pages/InventoryPage';
import CompaniesListPage from './pages/CompaniesListPage';
import CompanyProductsPage from './pages/CompanyProductsPage';
import TeamDetailPage from './pages/TeamDetailPage';
import MemberAssetsPage from './pages/MemberAssetsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import AllocationReportPage from './pages/AllocationReportPage';
import ExplorerPage from './pages/ExplorerPage';
import EconomicsProductsPage from './pages/EconomicsProductsPage';
import EconomicsProductDetailPage from './pages/EconomicsProductDetailPage';
import EconomicsClientsPage from './pages/EconomicsClientsPage';
import EconomicsClientDetailPage from './pages/EconomicsClientDetailPage';
import EconomicsSpendMixPage from './pages/EconomicsSpendMixPage';
import CompanyHierarchyPage from './pages/CompanyHierarchyPage';

const BASE_SUBNAV = [
  { to: '/dashboard/assets', label: 'Inventory', icon: Package, end: true },
  { to: '/dashboard/assets/companies', label: 'Companies', icon: Buildings },
  { to: '/dashboard/assets/explorer', label: 'Explorer', icon: TreeStructure },
  { to: '/dashboard/assets/allocation-report', label: 'Allocation Report', icon: ChartBar },
];

const ECONOMICS_SUBNAV = { to: '/dashboard/assets/economics/products', label: 'Economics', icon: CurrencyInr };
const COMPANY_HIERARCHY_SUBNAV = { to: '/dashboard/assets/company-hierarchy', label: 'Company Hierarchy', icon: Buildings };

function AssetsLayout() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const subnav = useMemo(
    () => (isSuperAdmin
      ? [...BASE_SUBNAV, ECONOMICS_SUBNAV, COMPANY_HIERARCHY_SUBNAV]
      : [...BASE_SUBNAV, COMPANY_HIERARCHY_SUBNAV]),
    [isSuperAdmin],
  );

  return (
    <ModuleShell
      title="Assets"
      subtitle={isSuperAdmin
        ? 'IT inventory & combined economics (Accounts billing + payroll CTC + asset cost)'
        : 'IT inventory, companies, and infrastructure'}
      icon={HardDrives}
      subnav={<ModuleSubnav items={subnav} aria-label="Assets navigation" />}
    >
      <Routes>
        <Route index element={<InventoryPage />} />
        <Route path="companies" element={<CompaniesListPage />} />
        <Route path="companies/:companyId" element={<CompanyProductsPage />} />
        <Route path="teams/:teamId" element={<TeamDetailPage />} />
        <Route path="teams/:teamId/members/:userId" element={<MemberAssetsPage />} />
        <Route path="teams/:teamId/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="explorer" element={<ExplorerPage />} />
        <Route path="allocation-report" element={<AllocationReportPage />} />
        <Route path="company-hierarchy" element={<CompanyHierarchyPage />} />
        {isSuperAdmin ? (
          <>
            <Route path="economics/products" element={<EconomicsProductsPage />} />
            <Route path="economics/products/:productId" element={<EconomicsProductDetailPage />} />
            <Route path="economics/clients" element={<EconomicsClientsPage />} />
            <Route path="economics/clients/:clientId" element={<EconomicsClientDetailPage />} />
            <Route path="economics/spend-mix" element={<EconomicsSpendMixPage />} />
            <Route path="economics/spend-mix/:category" element={<EconomicsSpendMixPage />} />
          </>
        ) : (
          <Route path="economics/*" element={<Navigate to="/dashboard/assets" replace />} />
        )}
        <Route path="*" element={<Navigate to="/dashboard/assets" replace />} />
      </Routes>
    </ModuleShell>
  );
}

export default AssetsLayout;
